import React from "react";
import { useEffect, useState, useRef } from "react";
import {
	Box,
	Typography,
	TableCell,
	Stack,
	TablePagination,
	Table,
	TableHead,
	TableRow,
	TableBody,
	InputAdornment,
	Button
} from "@mui/material";
import { GetClientDetails } from "../../services/client.services";
import { StyledTableCell, StyledTableContainer } from "../../components/Table";
import { useNavigate } from "react-router-dom";
import { StyledTextBox } from "../../components/TextBox";
import CircularProgress from "@mui/material/CircularProgress";
import { LoadingBox } from "../../components/Box";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Search, Download } from "lucide-react";
import { TextButton } from "../../components/Buttons";
import moment from "moment";

export default function OnboardingRequests() {
	const [clientData, setClientData] = useState(null);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [searchTerm, setSearchTerm] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const hasRun = useRef(false);

	const navigate = useNavigate();

	useEffect(() => {
		if (hasRun.current) return;
		hasRun.current = true;

		const fetchData = async () => {
			setIsLoading(false);
			try {
				await GetClientDetails('onBoarding').then((data) => {
					setIsLoading(true);
					setClientData(data);
				});
				setIsLoading(false);
			} catch (error) {
				throw error;
			}
		};

		fetchData();
	}, []);


	if (!clientData || isLoading) return (
		<LoadingBox>
			<CircularProgress />
		</LoadingBox>
	);

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};

	const filteredClients = clientData.filter(client => {
		const matchesSearch = (
			(client.firstName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
			(client.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
			(client.contactNo || '').toLowerCase().includes(searchTerm.toLowerCase())
		);

		return matchesSearch;
	});

	const parseDate = (str) => {
		if (!str) return null;
		// Remove AM/PM and trim
		const cleanStr = str.replace(/(AM|PM)/i, '').trim();
		return new Date(cleanStr);
	};

	const handleDownloadCSV = () => {
		if (!filteredClients || filteredClients.length === 0) return;

		const headers = ['First Name', 'Contact No', 'Unique ID', 'Business ID', 'Email', 'Application Completed'];
		
		const csvRows = filteredClients.map(client => {
			const date = parseDate(client.onboardedDate);
			const formattedDate = date ? moment(date).format("MMM D, y") : '';
			return [
				`"${client.firstName || ''}"`,
				`"${client.contactNo || ''}"`,
				`"${client.uniqueId || ''}"`,
				`"${client.businessId || ''}"`,
				`"${client.email || ''}"`,
				`"${formattedDate}"`
			].join(',');
		});

		const csvContent = [headers.join(','), ...csvRows].join('\n');
		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', 'onboarding_requests.csv');
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	return (
		<Box
			sx={{
				background: "#FFFFFF",
				boxShadow: "0px 2px 8px rgba(3, 40, 120, 0.18)",
				borderRadius: "18px",
			}}
			pl={5}
			pr={5}
			pb={5}
		>
			<Stack
				alignItems="center"
				direction="row"
				justifyContent="space-between"
				pt={1}
			>
				<StyledTextBox
					placeholder="Search clients by name, contact, or email..."
					variant="outlined"
					size="small"
					fullWidth
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Search size={20} color="#637381" />
							</InputAdornment>
						),
					}}
					sx={{
						maxWidth: 500,
						bgcolor: 'background.paper',
						'& .MuiOutlinedInput-root': {
							'& fieldset': {
								borderColor: 'divider',
							},
						},
					}}
				/>
				<Stack direction="row" alignItems="center" spacing={2}>
					<Button
						variant="outlined"
						startIcon={<Download size={18} />}
						onClick={handleDownloadCSV}
						sx={{
							textTransform: 'none',
							color: '#637381',
							borderColor: 'divider',
							borderRadius: '8px',
							height: '40px',
							'&:hover': {
								borderColor: '#637381',
								backgroundColor: 'transparent'
							}
						}}
					>
						Export CSV
					</Button>
					<TablePagination
						rowsPerPageOptions={[10, 25, 100]}
						component="div"
						count={filteredClients.length}
						rowsPerPage={rowsPerPage}
						page={page}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
					/>
				</Stack>
			</Stack>
			<StyledTableContainer>
				<Table aria-label="simple table">
					<TableHead>
						<TableRow sx={{ backgroundColor: "#F6F6F6" }}>
							<StyledTableCell align="left">Client Details</StyledTableCell>
							<StyledTableCell align="left">Business ID</StyledTableCell>
							<StyledTableCell align="left">Email</StyledTableCell>
							<StyledTableCell align="left">Application Completed</StyledTableCell>
							<StyledTableCell align="center">View</StyledTableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{filteredClients
							.sort((a, b) => {
								const dateA = parseDate(a.onboardedDate);
								const dateB = parseDate(b.onboardedDate);

								if (!dateA && !dateB) return 0;
								if (!dateA) return 1;
								if (!dateB) return -1;

								return dateB - dateA; // Descending
							})
							.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
							.map((row, index) => (
								<TableRow
									key={index}
									hover
									sx={{ cursor: 'pointer' }}
									onClick={() => navigate(`/client-admin/client-information/edit-information/${row.id}`)}>
									<TableCell align="left" variant="body2">
										<Stack>
											<Typography variant="body2">
												{row.firstName}
											</Typography>
											<Box sx={{
												'& .react-tel-input .form-control': {
													width: '100%',
													height: '28px',
													padding: '4px 8px 4px 48px',
													fontSize: '14px',
													border: 'none',
													backgroundColor: 'transparent',
													'&:hover, &:focus': {
														border: 'none',
														boxShadow: 'none',
													},
												},
												'& .react-tel-input .flag-dropdown': {
													border: 'none',
													backgroundColor: 'transparent',
												},
												'& .react-tel-input .selected-flag': {
													backgroundColor: 'transparent',
													'&:hover, &:focus': {
														backgroundColor: 'transparent',
													},
												},
											}}>
												<PhoneInput
													country={'us'}
													value={row.contactNo || ''}
													disabled
													disableDropdown
													enableSearch={false}
													inputStyle={{
														border: 'none',
														backgroundColor: 'transparent',
													}}
												/>
											</Box>
											<Typography variant="body3">
												<b>{row.uniqueId}</b>
											</Typography>
										</Stack>

									</TableCell>
									<TableCell align="left" variant="body2">
										{row.businessId}
									</TableCell>
									<TableCell align="left" variant="body2">
										{row.email}
									</TableCell>
									<TableCell align="left" variant="body2">
											{moment(parseDate(row.onboardedDate)).format("MMM D, y")}
									</TableCell>
									<TableCell align="center">
										<TextButton variant="text">Review</TextButton>
									</TableCell>
								</TableRow>
							))}
					</TableBody>
				</Table>
			</StyledTableContainer>
		</Box>
	);
}
